import { useState, useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { COI_ELIGIBLE_STATUSES, COI_RENEWAL_OFFER_STATUS, isCoiSelectablePolicy } from "@shared/schema";
import { isPolicyRecentlySynced } from "@shared/coi-sync-recency";
import type { CertificateHolder, Policy, Insured, PolicyAdditionalInsured, PolicyCoverages, PolicyVehicle, PhysicalDamageVehicleSelection } from "@shared/schema";
import {
  formatVehicleCoiLine,
  getPhysicalDamageFallbackDeductibles,
  isPhysicalDamageInactive,
} from "@shared/coi-vehicles";
import { getPhysicalDamageExcludedVins } from "@shared/physical-damage-vehicles";
import { useAdditionalInsureds, useLossPayees, useWaiversOfSubrogation, useCertificateHolder, useFeatureFlag, type InsuredAdditionalInsuredLink, type LossPayeeLink, type WaiverOfSubrogationLink } from "@/lib/api";
import type { CoiSendConfig } from "@shared/coi-template";
import { FLAG_COI_POLICY_PICKER } from "@shared/feature-flags";
import { trackEvent } from "@/lib/trackEvent";
import { PoliciesOnCertificatePicker } from "@/components/insured/coi/PoliciesOnCertificatePicker";
import type { PoliciesOnCertificatePickerChange } from "@/components/insured/coi/PoliciesOnCertificatePicker";
import {
  coiIssuedPolicyIdsByIssuance,
  countCoiPickerPolicyKinds,
  defaultCoiPolicySelection,
  expectedCoiCertificatePolicyIds,
  hasCoiPolicySetMismatch,
  isDefaultCoiPolicySelection,
  orderCoiPickerPolicies,
  summarizeCoiPolicySelection,
  withoutAorPolicies,
  COI_CUSTOM_SELECTION_TEMPLATE_NOTICE,
  COI_POLICY_NOT_SELECTABLE_MESSAGE,
  COI_POLICY_SET_MISMATCH_MESSAGE,
} from "@/components/insured/coi/policySelection";
import { CertificateHolderPicker } from "@/components/CertificateHolderPicker";
import { COIPreviewModal } from "@/components/insured/COIPreviewModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/queryClient";

// CSR-745 / CSR-795: parse a holder address string into the four dialog
// state slots. Tolerates the formats we have observed in production:
//   - "street, city, ST 12345"           (canonical, 3-comma)
//   - "street, city, ST 12345-6789"      (canonical, ZIP+4)
//   - "street, city, ST, 12345"          (4-comma, written by older
//                                          versions of this dialog)
//   - "street\ncity, ST 12345"           (newline-separated)
//   - "street\ncity, ST, 12345"          (newline-separated, 4-comma)
// Empty / whitespace-only input returns all-empty. When no recognized
// trailing state+zip is found, the whole string drops into `street` so
// the user can correct manually.
//
// Exported for direct unit testing (CSR-795). The function is otherwise
// only consumed by SendCOIDialog itself.
export function parseHolderAddress(addr: string): { street: string; city: string; state: string; zip: string } {
  if (!addr) return { street: "", city: "", state: "", zip: "" };
  const cleaned = addr.replace(/\r/g, "").trim();
  if (!cleaned) return { street: "", city: "", state: "", zip: "" };

  // CSR-1218: a postal token is either a US ZIP (5-digit, optional +4) or a
  // Canadian postal code (A1A 1A1, optional space, case-insensitive).
  const POSTAL = "\\d{5}(?:-\\d{4})?|[A-Za-z]\\d[A-Za-z]\\s?\\d[A-Za-z]\\d";

  // Newline-separated: street on prior line(s), city/state/zip on the last line.
  const lines = cleaned.split("\n").map((s) => s.trim()).filter(Boolean);
  if (lines.length >= 2) {
    const last = lines[lines.length - 1];
    const m = last.match(new RegExp(`^(.*?),?\\s*([A-Z]{2})[,\\s]+(${POSTAL})$`));
    if (m) {
      return {
        street: lines.slice(0, -1).join(", "),
        city: m[1].trim(),
        state: m[2],
        zip: m[3],
      };
    }
  }

  // Single-line: "street, city, ST zip" OR "street, city, ST, zip".
  const parts = cleaned.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 3) {
    const last = parts[parts.length - 1];

    // Case A: 3+ comma-parts, last = "ST zip" (canonical).
    const stateZipMatch = last.match(new RegExp(`^([A-Z]{2})\\s+(${POSTAL})$`));
    if (stateZipMatch) {
      return {
        street: parts.slice(0, parts.length - 2).join(", "),
        city: parts[parts.length - 2],
        state: stateZipMatch[1],
        zip: stateZipMatch[2],
      };
    }

    // Case B: 4+ comma-parts, last = "zip" alone, second-to-last = "ST".
    const zipOnlyMatch = last.match(new RegExp(`^(${POSTAL})$`));
    const stateOnlyMatch = parts[parts.length - 2].match(/^[A-Z]{2}$/);
    if (zipOnlyMatch && stateOnlyMatch) {
      return {
        street: parts.slice(0, parts.length - 3).join(", "),
        city: parts[parts.length - 3],
        state: parts[parts.length - 2],
        zip: zipOnlyMatch[1],
      };
    }
  }

  return { street: addr, city: "", state: "", zip: "" };
}
import {
  Check,
  FileText,
  Plus,
  X,
} from "lucide-react";
import { US_STATE_OPTIONS, CA_PROVINCE_OPTIONS } from "@/lib/us-states";

// CSR-1097: lightweight client-side email-format check. Mirrors the server's
// own regex (server/routes.ts:4778) so this visual "first line of defense"
// agrees with the server's FAILED-row determination. A malformed address is
// NOT blocked on submit — it still POSTs and the server records a FAILED
// history row for it (DoD #5); this only surfaces an inline warning.
const RECIPIENT_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isRecipientInvalid = (value: string): boolean => {
  const trimmed = value.trim();
  return trimmed !== "" && !RECIPIENT_EMAIL_RE.test(trimmed);
};

interface VehicleItem { id: string; label: string }
interface DriverItem { id: string; label: string }
interface TrailerItem { id: string; label: string }

// CSR-2287: unchecking a policy shrinks the asset lists but never the ids the
// producer already ticked, and a stale id still reaches the description, the
// history tags and the resend snapshot. Keeps the selection's own order.
const keepListedAssetIds = (selected: string[], listed: { id: string }[]) => {
  const listedIds = new Set(listed.map((item) => item.id));
  return selected.filter((id) => listedIds.has(id));
};

interface SendCOIDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insured: Insured;
  policies: Policy[];
  // CSR-2333: with the AOR flag on, an AOR'd policy never goes on a certificate.
  showAor?: boolean;
  // CSR-1791: when set, the dialog opens in "Resend COI" mode pre-filled from
  // this config instead of resetting to empty.
  resendConfig?: CoiSendConfig | null;
}

export function SendCOIDialog({ open, onOpenChange, insured, policies, showAor = false, resendConfig }: SendCOIDialogProps) {
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isResend = !!resendConfig;

  // CSR-778: the dialog no longer asks the user to pick a single policy. The
  // server aggregates every COI-eligible policy under the insured. We still
  // pass a "primary" policy id on the URL for the worker's carrier-sync
  // touch (`ensurePolicySyncedForCOI`) — the first eligible policy.
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  // CSR-1405: when the primary policy was scraped from the carrier within the
  // recency window, sending opens this confirmation instead of firing the send
  // immediately so the producer can choose to sync (or skip) before sending.
  const [confirmSyncOpen, setConfirmSyncOpen] = useState(false);

  // CSR-2287/CSR-2250 — producer-facing policy picker. Every read of this
  // flag below keeps the OFF path what it was: no picker, no
  // `selectedPolicyIds` on either body, no new events.
  const policyPickerEnabled = useFeatureFlag(FLAG_COI_POLICY_PICKER);
  const [chosenPolicyIds, setSelectedPolicyIds] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingEmptySelectionFocus, setPendingEmptySelectionFocus] = useState(false);
  const emptyErrorRef = useRef<HTMLDivElement>(null);
  const openTrackedRef = useRef(false);
  const missingLimitsTrackedRef = useRef<number | null>(null);

  const [certificateHolder, setCertificateHolder] = useState("");
  // CSR-745: tracks the selected `certificate_holders.id` when the user
  // picks an existing row from the picker. Cleared when the user types a
  // new name. Forwarded as `certificateHolderId` on the COI POST bodies
  // so the server can skip auto-create for already-known holders.
  const [selectedHolderId, setSelectedHolderId] = useState<string | null>(null);
  const [holderAddress, setHolderAddress] = useState("");
  const [holderCity, setHolderCity] = useState("");
  const [holderState, setHolderState] = useState("");
  const [holderZip, setHolderZip] = useState("");
  // CSR-1097: up to 5 typed "To Email" recipients. Starts with one empty
  // field. `copyTouched` tracks whether the user has manually toggled the
  // copy-to-insured checkbox; until then the checkbox follows a derived
  // default (checked when all To fields are blank and the insured has an email).
  const [recipients, setRecipients] = useState<string[]>([""]);
  const [copyTouched, setCopyTouched] = useState(false);

  const [additionalInfo, setAdditionalInfo] = useState<string[]>([]);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[]>([]);
  const [selectedDriverIds, setSelectedDriverIds] = useState<string[]>([]);
  const [specialVerbiage, setSpecialVerbiage] = useState("");
  const [sendCopyToInsured, setSendCopyToInsured] = useState(false);
  const [selectedTrailerIds, setSelectedTrailerIds] = useState<string[]>([]);
  const [additionalInsuredName, setAdditionalInsuredName] = useState("");
  // CSR-741: when a selected AI is one of the linked rows
  // (`InsuredAdditionalInsuredLink`), capture its persisted `coverageIds`
  // and `specialVerbiage` so the COI can mark ADDL INSD per coverage and
  // honor any special verbiage attached to the entity. DC-synced AI names
  // (no link row) leave this empty — the default phrasing is used and the
  // legacy "apply to every row" behavior holds.
  const [additionalInsuredCoverageIds, setAdditionalInsuredCoverageIds] = useState<string[]>([]);
  const [additionalInsuredSpecialVerbiage, setAdditionalInsuredSpecialVerbiage] = useState<string | null>(null);
  // CSR-792 — Loss Payee state.
  const [lossPayeeId, setLossPayeeId] = useState<string | null>(null);
  const [lossPayeeName, setLossPayeeName] = useState("");
  const [lossPayeeCoverageIds, setLossPayeeCoverageIds] = useState<string[]>([]);
  const [lossPayeeVehicles, setLossPayeeVehicles] = useState<PolicyVehicle[]>([]);
  const [lossPayeeSpecialVerbiage, setLossPayeeSpecialVerbiage] = useState<string | null>(null);
  // CSR-1101 — Waiver of Subrogation state (single selection, mirrors LP).
  const [waiverOfSubrogationId, setWaiverOfSubrogationId] = useState<string | null>(null);
  const [waiverOfSubrogationName, setWaiverOfSubrogationName] = useState("");
  const [waiverOfSubrogationCoverageIds, setWaiverOfSubrogationCoverageIds] = useState<string[]>([]);
  const [waiverOfSubrogationSpecialVerbiage, setWaiverOfSubrogationSpecialVerbiage] = useState<string | null>(null);

  const { data: additionalInsuredLinks } = useAdditionalInsureds(insured.id);
  // CSR-2274 — disabling the picker row is not enough, because a selection can
  // arrive without one: a resend restores the saved name, and a selected AI can
  // go inactive when this query refetches. Either way the row renders disabled
  // while the name state still holds it, so the send/preview gates must consult
  // the link, not the picker. Same shape as the CSR-1316 loss-payee guard.
  const selectedAdditionalInsuredIsInactive = useMemo(() => {
    const name = additionalInsuredName.trim();
    if (!name) return false;
    return (additionalInsuredLinks ?? []).some(
      (link) => link.inactive && link.name === name,
    );
  }, [additionalInsuredLinks, additionalInsuredName]);
  // CSR-2212 — `isLoading` matters here, not just the data. These two queries
  // now reach the COI service for their "COIs sent" figures, so they can be
  // slow in a way they never were as local reads. Without distinguishing
  // pending from empty, the picker below states as fact that the insured has
  // no loss payee while the answer is still in flight — and a producer who
  // believes it sends a certificate without one.
  const { data: lossPayeeLinks, isLoading: lossPayeesLoading } = useLossPayees(insured.id);
  const { data: waiverOfSubrogationLinks, isLoading: waiversLoading } =
    useWaiversOfSubrogation(insured.id);
  // CSR-1791: legacy rows have no snapshot address; derive it from the stored
  // certificate holder id so the pre-filled modal is complete.
  const legacyHolderId =
    resendConfig && !resendConfig.holderAddress ? resendConfig.selectedHolderId : null;
  const { data: legacyHolder } = useCertificateHolder(legacyHolderId);

  const aorOptions = useMemo(() => ({ excludeAor: showAor }), [showAor]);
  const certificatePolicies = useMemo(
    () => withoutAorPolicies(policies, aorOptions),
    [policies, aorOptions],
  );
  // CSR-2333: the flag can resolve, or a refetch can flag a policy, while the
  // dialog is open with that policy already checked. Derived rather than
  // synced, so no render (or Send) ever carries an AOR'd id — and the healthy
  // checks survive, where the service would 422 the whole set.
  const selectedPolicyIds = useMemo(() => {
    const certificateIds = new Set(certificatePolicies.map((p) => p.id));
    const excludedIds = new Set(
      policies.filter((p) => !certificateIds.has(p.id)).map((p) => p.id),
    );
    return chosenPolicyIds.filter((id) => !excludedIds.has(id));
  }, [policies, certificatePolicies, chosenPolicyIds]);

  const eligiblePolicies = useMemo(
    () =>
      certificatePolicies.filter((p) =>
        COI_ELIGIBLE_STATUSES.has((p.status || "").toLowerCase()),
      ),
    [certificatePolicies],
  );

  // CSR-778: the "primary" policy is the first eligible policy after the
  // server-side stable sort (effectiveDate ASC, tiebreak by id ASC). It's
  // only used as the URL placeholder for the worker's carrier-sync touch;
  // every aggregation decision on the server runs against the full insured.
  const primaryPolicy = eligiblePolicies[0];

  // CSR-2287: the picker's "can go on a certificate" is NOT
  // COI_ELIGIBLE_STATUSES — it drops reinstated / non-renewal terms and adds a
  // renewal offer whose term has not started, so it is the gate the dialog uses
  // while the flag is on.
  const selectablePolicies = useMemo(
    () => certificatePolicies.filter((p) => isCoiSelectablePolicy(p)),
    [certificatePolicies],
  );

  // The terms this certificate is built from. With the flag OFF this IS
  // `eligiblePolicies`, so the asset lists and the payload are byte-identical.
  const checkedPolicies = useMemo(() => {
    if (!policyPickerEnabled) return eligiblePolicies;
    const checked = new Set(selectedPolicyIds);
    return certificatePolicies.filter((p) => checked.has(p.id));
  }, [policyPickerEnabled, eligiblePolicies, certificatePolicies, selectedPolicyIds]);

  // The URL's `:id` stays the carrier-sync hint it always was, but under a
  // selection it has to name a policy that is actually on the certificate.
  const sendPolicy = useMemo(() => {
    if (!policyPickerEnabled) return primaryPolicy;
    return orderCoiPickerPolicies(checkedPolicies)[0] ?? primaryPolicy;
  }, [policyPickerEnabled, checkedPolicies, primaryPolicy]);
  const sendPolicyId = sendPolicy?.id ?? "";

  const selectionSummary = useMemo(
    () => summarizeCoiPolicySelection(certificatePolicies, selectedPolicyIds),
    [certificatePolicies, selectedPolicyIds],
  );
  const selectionIsDefault = isDefaultCoiPolicySelection(certificatePolicies, selectedPolicyIds);
  const selectionIsEmpty = policyPickerEnabled && selectedPolicyIds.length === 0;
  const includesRenewalOffer = checkedPolicies.some(
    (p) => (p.status ?? "").trim().toLowerCase() === COI_RENEWAL_OFFER_STATUS,
  );

  // Shared analytics shape for Previewed / Sent. Statuses and counts only —
  // no policy numbers, holder names or emails.
  const selectionEventProps = () => ({
    selected_count: checkedPolicies.length,
    statuses_included: Array.from(
      new Set(checkedPolicies.map((p) => (p.status ?? "").trim().toLowerCase())),
    ).sort(),
    is_default: selectionIsDefault,
    includes_renewal_offer: includesRenewalOffer,
    any_missing_limits: selectionSummary.selectedMissingLimitsCount > 0,
    entry: isResend ? "resend" : "new",
  });

  // CSR-778: vehicles / drivers / trailers picker lists aggregate across
  // every eligible policy on this insured — CSR-2287: across the CHECKED
  // policies while the picker flag is on, so an asset can never be listed on a
  // certificate whose policy the producer left off. Dedupe by VIN / license
  // number (or fallback synthetic id) so a vehicle on two policies shows
  // once. `removedAt` entries are dropped.
  const vehicles = useMemo<VehicleItem[]>(() => {
    const out: VehicleItem[] = [];
    const seen = new Set<string>();
    for (const policy of checkedPolicies) {
      const pdFallback = getPhysicalDamageFallbackDeductibles(
        policy.coverages as PolicyCoverages | null,
      );
      const pdExcludedVins = getPhysicalDamageExcludedVins({
        vehicles: policy.vehicles as PolicyVehicle[] | null,
        trailers: policy.trailers as PolicyVehicle[] | null,
        coverages: policy.coverages as PolicyCoverages | null,
        physicalDamageVehicles: policy.physicalDamageVehicles as PhysicalDamageVehicleSelection | null,
      });
      const pdInactive = isPhysicalDamageInactive(policy.coverages as PolicyCoverages | null);
      const raw = (policy.vehicles ?? []) as PolicyVehicle[];
      for (let i = 0; i < raw.length; i++) {
        const v = raw[i];
        if (!v || v.removedAt) continue;
        const id = (v.vin as string) || `vehicle-${policy.id}-${i}`;
        if (seen.has(id)) continue;
        seen.add(id);
        const formatted = formatVehicleCoiLine(v, pdFallback, {
          suppressPhysicalDamage: pdInactive || (!!v.vin && pdExcludedVins.has(v.vin)),
        });
        out.push({ id, label: formatted || `Vehicle ${out.length + 1}` });
      }
    }
    return out;
  }, [checkedPolicies]);

  const drivers = useMemo<DriverItem[]>(() => {
    const out: DriverItem[] = [];
    const seen = new Set<string>();
    for (const policy of checkedPolicies) {
      const raw = (policy.drivers ?? []) as Array<Record<string, unknown>>;
      for (let i = 0; i < raw.length; i++) {
        const d = raw[i];
        if (!d || d.removedAt) continue;
        const id = (d.licenseNumber as string) || `driver-${policy.id}-${i}`;
        if (seen.has(id)) continue;
        seen.add(id);
        out.push({ id, label: (d.name as string) || `Driver ${out.length + 1}` });
      }
    }
    return out;
  }, [checkedPolicies]);

  const trailers = useMemo<TrailerItem[]>(() => {
    const out: TrailerItem[] = [];
    const seen = new Set<string>();
    for (const policy of checkedPolicies) {
      const pdFallback = getPhysicalDamageFallbackDeductibles(
        policy.coverages as PolicyCoverages | null,
      );
      const pdExcludedVins = getPhysicalDamageExcludedVins({
        vehicles: policy.vehicles as PolicyVehicle[] | null,
        trailers: policy.trailers as PolicyVehicle[] | null,
        coverages: policy.coverages as PolicyCoverages | null,
        physicalDamageVehicles: policy.physicalDamageVehicles as PhysicalDamageVehicleSelection | null,
      });
      const pdInactive = isPhysicalDamageInactive(policy.coverages as PolicyCoverages | null);
      const raw = (policy.trailers ?? []) as PolicyVehicle[];
      for (let i = 0; i < raw.length; i++) {
        const t = raw[i];
        if (!t || t.removedAt) continue;
        const id = (t.vin as string) || `trailer-${policy.id}-${i}`;
        if (seen.has(id)) continue;
        seen.add(id);
        const formatted = formatVehicleCoiLine(t, pdFallback, {
          suppressPhysicalDamage: pdInactive || (!!t.vin && pdExcludedVins.has(t.vin)),
        });
        out.push({ id, label: formatted || `Trailer ${out.length + 1}` });
      }
    }
    return out;
  }, [checkedPolicies]);

  // CSR-2287: what the certificate is actually built from. Every consumer of a
  // ticked asset reads these, so an asset whose policy was unchecked cannot
  // reach the certificate, the snapshot or the tags. Flag OFF returns the raw
  // arrays untouched — same reference, so today's bodies are byte-identical.
  const effectiveSelectedVehicleIds = useMemo(
    () =>
      policyPickerEnabled
        ? keepListedAssetIds(selectedVehicleIds, vehicles)
        : selectedVehicleIds,
    [policyPickerEnabled, selectedVehicleIds, vehicles],
  );
  const effectiveSelectedTrailerIds = useMemo(
    () =>
      policyPickerEnabled
        ? keepListedAssetIds(selectedTrailerIds, trailers)
        : selectedTrailerIds,
    [policyPickerEnabled, selectedTrailerIds, trailers],
  );
  const effectiveSelectedDriverIds = useMemo(
    () =>
      policyPickerEnabled
        ? keepListedAssetIds(selectedDriverIds, drivers)
        : selectedDriverIds,
    [policyPickerEnabled, selectedDriverIds, drivers],
  );

  const toggleAdditionalInfo = (value: string) => {
    setAdditionalInfo((prev) => {
      const removing = prev.includes(value);
      if (!removing) {
        if (value === "Vehicles") setSelectedVehicleIds(vehicles.map((v) => v.id));
        if (value === "Trailers") setSelectedTrailerIds(trailers.map((t) => t.id));
        if (value === "Drivers") setSelectedDriverIds(drivers.map((d) => d.id));
      }
      return removing ? prev.filter((v) => v !== value) : [...prev, value];
    });
  };
  const toggleVehicle = (id: string) => {
    setSelectedVehicleIds((prev) => prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]);
  };
  const toggleDriver = (id: string) => {
    setSelectedDriverIds((prev) => prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]);
  };
  const toggleTrailer = (id: string) => {
    setSelectedTrailerIds((prev) => prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]);
  };

  const buildDescription = () => {
    const parts: string[] = [];
    if (
      additionalInfo.includes("Vehicles") &&
      effectiveSelectedVehicleIds.length
    ) {
      const labels = vehicles.filter((v) => effectiveSelectedVehicleIds.includes(v.id)).map((v) => v.label);
      parts.push(`Vehicles:\n${labels.map((l) => `- ${l}`).join("\n")}`);
    }
    if (
      additionalInfo.includes("Trailers") &&
      effectiveSelectedTrailerIds.length
    ) {
      const labels = trailers.filter((t) => effectiveSelectedTrailerIds.includes(t.id)).map((t) => t.label);
      parts.push(`Trailers:\n${labels.map((l) => `- ${l}`).join("\n")}`);
    }
    if (
      additionalInfo.includes("Drivers") &&
      effectiveSelectedDriverIds.length
    ) {
      const labels = drivers.filter((d) => effectiveSelectedDriverIds.includes(d.id)).map((d) => d.label);
      parts.push(`Drivers:\n${labels.map((l) => `- Name: ${l}`).join("\n")}`);
    }
    // CSR-2163 — the Additional Insured and Waiver of Subrogation sentences are
    // NO LONGER composed here. They are composed server-side, from the names and
    // entity verbiage threaded alongside this description, so that every send
    // path produces them — not just this dialog. While they were built here, any
    // caller that set the AI flag without going through this component produced a
    // certificate with ADDL INSD "Y" and nobody named (CSR-2160).
    //
    // The OQ-1 rule (a WoS line subsumes the standalone AI line for the same
    // entity) moved with them, into `composeEntityRoleStatements`.
    //
    // What stays here is what genuinely belongs to the dialog: the asset blocks
    // the producer ticked, and the certificate-wide Special Verbiage remark.
    if (additionalInfo.includes("Special Verbiage") && specialVerbiage.trim()) {
      parts.push(specialVerbiage.trim());
    }
    return parts.length > 0 ? parts.join("\n\n") : undefined;
  };

  // CSR-795: compose state + zip as a single segment so the persisted
  // address follows the canonical "street, city, ST zip" 3-comma form.
  const stateZip = [holderState, holderZip].filter(Boolean).join(" ");
  const fullAddress = [holderAddress, holderCity, stateZip].filter(Boolean).join(", ");

  // CSR-1097: recipient field mutators + derived copy-to-insured default.
  const updateRecipient = (index: number, value: string) => {
    setRecipients((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };
  const addRecipient = () => {
    setRecipients((prev) => (prev.length >= 5 ? prev : [...prev, ""]));
  };
  const removeRecipient = (index: number) => {
    setRecipients((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const allRecipientsBlank = recipients.every((r) => !r.trim());
  const effectiveSendCopy = copyTouched
    ? sendCopyToInsured
    : allRecipientsBlank && !!insured.email;

  const resetForm = () => {
    setCertificateHolder("");
    setSelectedHolderId(null);
    setHolderAddress("");
    setHolderCity("");
    setHolderState("");
    setHolderZip("");
    setRecipients([""]);
    setCopyTouched(false);
    setAdditionalInfo([]);
    setSelectedVehicleIds([]);
    setSelectedDriverIds([]);
    setSelectedTrailerIds([]);
    setSendCopyToInsured(false);
    setSpecialVerbiage("");
    setAdditionalInsuredName("");
    setAdditionalInsuredCoverageIds([]);
    setAdditionalInsuredSpecialVerbiage(null);
    setLossPayeeId(null);
    setLossPayeeName("");
    setLossPayeeCoverageIds([]);
    setLossPayeeVehicles([]);
    setLossPayeeSpecialVerbiage(null);
    setWaiverOfSubrogationId(null);
    setWaiverOfSubrogationName("");
    setWaiverOfSubrogationCoverageIds([]);
    setWaiverOfSubrogationSpecialVerbiage(null);
    setPreviewLoading(false);
    setPdfBlob(null);
    setPdfData(null);
    setPreviewOpen(false);
  };

  const applyConfig = (cfg: CoiSendConfig) => {
    setCertificateHolder(cfg.certificateHolder);
    setSelectedHolderId(cfg.selectedHolderId);
    setHolderAddress(cfg.holderAddress);
    setHolderCity(cfg.holderCity);
    setHolderState(cfg.holderState);
    setHolderZip(cfg.holderZip);
    setRecipients(cfg.recipients.length ? cfg.recipients : [""]);
    setCopyTouched(true);
    setSendCopyToInsured(cfg.sendCopyToInsured);
    setAdditionalInfo(cfg.additionalInfo);
    setSelectedVehicleIds(cfg.selectedVehicleIds);
    setSelectedDriverIds(cfg.selectedDriverIds);
    setSelectedTrailerIds(cfg.selectedTrailerIds);
    setSpecialVerbiage(cfg.specialVerbiage);
    setAdditionalInsuredName(cfg.additionalInsuredName);
    setAdditionalInsuredCoverageIds(cfg.additionalInsuredCoverageIds);
    setAdditionalInsuredSpecialVerbiage(cfg.additionalInsuredSpecialVerbiage);
    setLossPayeeId(cfg.lossPayeeId);
    setLossPayeeName(cfg.lossPayeeName);
    setLossPayeeCoverageIds(cfg.lossPayeeCoverageIds);
    setLossPayeeVehicles(cfg.lossPayeeVehicles);
    setLossPayeeSpecialVerbiage(cfg.lossPayeeSpecialVerbiage);
    setWaiverOfSubrogationId(cfg.waiverOfSubrogationId);
    setWaiverOfSubrogationName(cfg.waiverOfSubrogationName);
    setWaiverOfSubrogationCoverageIds(cfg.waiverOfSubrogationCoverageIds);
    setWaiverOfSubrogationSpecialVerbiage(cfg.waiverOfSubrogationSpecialVerbiage);
    setPreviewLoading(false);
    setPdfBlob(null);
    setPdfData(null);
    setPreviewOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    if (resendConfig) {
      applyConfig(resendConfig);
    } else {
      resetForm();
    }
    // CSR-2287: a resend never replays the original policy set — `CoiSendConfig`
    // holds no selection, and a term in force then may be expired now. Both
    // entries open on today's default; the accordion only opens by itself when
    // that default is empty, which is the one case the producer must act on.
    const defaultSelection = defaultCoiPolicySelection(policies, aorOptions);
    setSelectedPolicyIds(defaultSelection);
    setPickerOpen(defaultSelection.length === 0);
    setPendingEmptySelectionFocus(false);
    missingLimitsTrackedRef.current = null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Separate from the reset above because the flag can resolve after the dialog
  // is already open (the flag list is a query), and the open event must still
  // fire exactly once per open. CSR-2287.
  useEffect(() => {
    if (!open) {
      openTrackedRef.current = false;
      return;
    }
    if (!policyPickerEnabled || openTrackedRef.current) return;
    openTrackedRef.current = true;
    const counts = countCoiPickerPolicyKinds(policies, undefined, aorOptions);
    trackEvent("COI Send Modal Opened", {
      entry: isResend ? "resend" : "new",
      in_force_count: counts.inForce,
      renewal_offer_count: counts.renewalOffer,
      locked_count: counts.locked,
      default_selection_empty: defaultCoiPolicySelection(policies, aorOptions).length === 0,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, policyPickerEnabled]);

  // Once per distinct visible warning state, not per render.
  useEffect(() => {
    if (!open || !policyPickerEnabled) return;
    const count = selectionSummary.selectedMissingLimitsCount;
    if (count === 0) {
      missingLimitsTrackedRef.current = 0;
      return;
    }
    if (missingLimitsTrackedRef.current === count) return;
    missingLimitsTrackedRef.current = count;
    trackEvent("COI Missing Limits Warning Shown", {
      selected_missing_limits_count: count,
    });
  }, [open, policyPickerEnabled, selectionSummary.selectedMissingLimitsCount]);

  // The empty error mounts with the accordion, so the focus move has to wait
  // for the commit that expands it. CSR-2287.
  useEffect(() => {
    if (!pendingEmptySelectionFocus) return;
    emptyErrorRef.current?.focus();
    setPendingEmptySelectionFocus(false);
  }, [pendingEmptySelectionFocus]);

  const handleSelectionChange = (
    next: string[],
    change: PoliciesOnCertificatePickerChange,
  ) => {
    setSelectedPolicyIds(next);
    trackEvent("COI Policy Selection Changed", {
      action: change.action,
      status: (change.status ?? "").trim().toLowerCase() || null,
      selected_count: next.length,
      is_default: isDefaultCoiPolicySelection(certificatePolicies, next),
    });
  };

  const handlePickerOpenChange = (next: boolean) => {
    setPickerOpen(next);
    if (next) trackEvent("COI Policy Picker Expanded");
  };

  // CSR-2287: the empty-selection lock. The buttons carry `aria-disabled`
  // rather than `disabled` so a click still reaches this guard and says why,
  // instead of leaving the producer with a dead control.
  const blockedOnEmptySelection = (): boolean => {
    if (!selectionIsEmpty) return false;
    setPickerOpen(true);
    setPendingEmptySelectionFocus(true);
    trackEvent("COI Send Blocked No Policy Selected");
    return true;
  };

  useEffect(() => {
    if (!open || !legacyHolder || holderAddress) return;
    if (legacyHolder.address) {
      const { street, city, state, zip } = parseHolderAddress(legacyHolder.address);
      setHolderAddress(street);
      setHolderCity(city);
      setHolderState(state);
      setHolderZip(zip);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, legacyHolder]);

  const handlePreview = async () => {
    if (blockedOnEmptySelection()) return;
    if (!sendPolicyId) {
      toast({
        title: "No COI-eligible policies",
        description: "This insured has no policies eligible for a Certificate of Insurance.",
        variant: "destructive",
      });
      return;
    }
    if (certificateHolder.trim()) {
      const missingFields: string[] = [];
      if (!holderAddress.trim()) missingFields.push("Address");
      if (!holderCity.trim()) missingFields.push("City");
      if (!holderState.trim()) missingFields.push("State");
      if (!holderZip.trim()) missingFields.push("Zip");
      if (missingFields.length > 0) {
        toast({
          title: "Missing required fields",
          description: `When a Certificate Holder is specified, ${missingFields.join(", ")} ${missingFields.length === 1 ? "is" : "are"} required.`,
          variant: "destructive",
        });
        return;
      }
    }
    setPreviewLoading(true);
    try {
      // CSR-1453: the "Additional Insured" tab being open is NOT a selection.
      // ADDL INSD must only render "Y" when an AI entity is genuinely picked
      // (a non-empty name), matching the Description-of-Operations line gate.
      // Otherwise the server's legacy "apply to every row" back-compat path
      // marked every coverage row "Y" with no AI line in the description.
      const hasAi =
        additionalInfo.includes("Additional Insured") &&
        additionalInsuredName.trim().length > 0 &&
        !selectedAdditionalInsuredIsInactive;
      const hasWaiverOfSubrogation = additionalInfo.includes("Waiver of Subrogation");
      const effectiveWaiverOfSubrogationId =
        hasWaiverOfSubrogation && waiverOfSubrogationId ? waiverOfSubrogationId : null;
      // CSR-1316: only forward the LP when the checkbox is still checked —
      // the picker state survives unchecking, and a stale id would make the
      // server render the LP line anyway.
      const effectiveLossPayeeId =
        additionalInfo.includes("Loss Payee") && lossPayeeId ? lossPayeeId : null;
      const res = await apiFetch("/api/coi/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          // CSR-778: preview the multi-policy COI by passing `insuredId`.
          // The server aggregates every COI-eligible policy under the insured.
          insuredId: insured.id,
          certificateHolderName: certificateHolder,
          certificateHolderAddress: fullAddress,
          additionalInsured: hasAi,
          // CSR-2163: the server composes the AI / WoS sentences from these.
          // Sent only when the entity is genuinely picked, which is what keeps
          // the flag and the sentence from ever disagreeing.
          additionalInsuredName: hasAi ? additionalInsuredName.trim() : undefined,
          additionalInsuredSpecialVerbiage: hasAi ? additionalInsuredSpecialVerbiage : undefined,
          waiverOfSubrogationName: effectiveWaiverOfSubrogationId
            ? waiverOfSubrogationName.trim()
            : undefined,
          waiverOfSubrogationSpecialVerbiage: effectiveWaiverOfSubrogationId
            ? waiverOfSubrogationSpecialVerbiage
            : undefined,
          additionalInsuredCoverageIds:
            hasAi && additionalInsuredCoverageIds.length > 0
              ? additionalInsuredCoverageIds
              : undefined,
          waiverOfSubrogation: false,
          description: buildDescription(),
          // CSR-792 — Loss Payee fields (CSR-1316: gated on the checkbox).
          lossPayeeId: effectiveLossPayeeId,
          lossPayeeCoverageIds: effectiveLossPayeeId ? lossPayeeCoverageIds : [],
          lossPayeeVehicles: effectiveLossPayeeId ? lossPayeeVehicles : [],
          lossPayeeSpecialVerbiage: effectiveLossPayeeId ? lossPayeeSpecialVerbiage : null,
          // CSR-1101 — Waiver of Subrogation fields. Coverage ids only flow when
          // a WoS is selected; the description WoS line is composed client-side
          // and rides in `description`.
          waiverOfSubrogationId: effectiveWaiverOfSubrogationId,
          waiverOfSubrogationCoverageIds: effectiveWaiverOfSubrogationId
            ? waiverOfSubrogationCoverageIds
            : undefined,
          // CSR-2287: sent on every flag-ON preview, default or not — the
          // server decides which is which, and an absent key means "no
          // selection was made", which is a different request.
          ...(policyPickerEnabled ? { selectedPolicyIds } : {}),
        }),
      });
      if (!res.ok) {
        // A 422 names a term the server will not put on a certificate. Say that
        // instead of the generic preview failure, and leave the selection alone
        // so the producer can see which row to change. CSR-2287.
        if (policyPickerEnabled && res.status === 422) {
          const body = await res.json().catch(() => null);
          if (body?.error === "policy_not_selectable") {
            toast({
              title: "Policy can’t be on this certificate",
              description: COI_POLICY_NOT_SELECTABLE_MESSAGE,
              variant: "destructive",
            });
            return;
          }
        }
        throw new Error("Failed to generate preview");
      }
      const blob = new Blob([await res.blob()], { type: "application/pdf" });
      const buf = await blob.arrayBuffer();
      setPdfBlob(blob);
      setPdfData(buf);
      setPreviewOpen(true);
      if (policyPickerEnabled) {
        trackEvent("COI Previewed", selectionEventProps());
      }
    } catch {
      toast({ title: "Error", description: "Failed to generate PDF preview.", variant: "destructive" });
    } finally {
      setPreviewLoading(false);
    }
  };

  // CSR-1097: collect typed recipients (trim, drop empties, dedupe
  // case-insensitively). The insured copy is a separate flag, not one of the
  // typed 5 — the server resolves and appends it. A malformed address is NOT
  // dropped here: the server records a FAILED history row for that recipient
  // only while the valid recipients still send (DoD rule #5).
  const collectTypedRecipients = (): string[] => {
    const out: string[] = [];
    const seenLower = new Set<string>();
    for (const raw of recipients) {
      const email = raw.trim();
      if (!email) continue;
      const lower = email.toLowerCase();
      if (seenLower.has(lower)) continue;
      seenLower.add(lower);
      out.push(email);
    }
    return out;
  };

  const handleSend = () => {
    if (blockedOnEmptySelection()) return;
    if (!sendPolicyId) {
      toast({
        title: "No COI-eligible policies",
        description: "This insured has no policies eligible for a Certificate of Insurance.",
        variant: "destructive",
      });
      return;
    }
    if (certificateHolder.trim()) {
      const missingFields: string[] = [];
      if (!holderAddress.trim()) missingFields.push("Address");
      if (!holderCity.trim()) missingFields.push("City");
      if (!holderState.trim()) missingFields.push("State");
      if (!holderZip.trim()) missingFields.push("Zip");
      if (missingFields.length > 0) {
        toast({
          title: "Missing required fields",
          description: `When a Certificate Holder is specified, ${missingFields.join(", ")} ${missingFields.length === 1 ? "is" : "are"} required.`,
          variant: "destructive",
        });
        return;
      }
    }

    // Guard: nothing to send when there are no typed recipients and the copy
    // is off. Do NOT close the dialog so the user can correct.
    if (collectTypedRecipients().length === 0 && !effectiveSendCopy) {
      toast({
        title: "No recipients",
        description: "Add at least one recipient or enable Send copy to insured.",
        variant: "destructive",
      });
      return;
    }

    // CSR-1405: when the primary policy's carrier data is already fresh (scraped
    // within the recency window), the automatic send-time sync is unnecessary.
    // Ask the producer whether to sync first instead of always blocking on it.
    // Stale (or never-scraped) policies skip the prompt and sync as before.
    // CSR-2287: ask about the policy whose id this send actually carries —
    // under a selection that is the first checked term, not the first eligible
    // one, and it is the policy the worker will sync.
    if (isPolicyRecentlySynced(sendPolicy?.carrierScrapedAt ?? sendPolicy?.syncedAt)) {
      setConfirmSyncOpen(true);
      return;
    }

    performSend(false);
  };

  // CSR-1405: validation already passed in handleSend (or the confirm modal).
  // `skipSync` tells the server whether to run the send-time carrier sync.
  const performSend = (skipSync: boolean) => {
    setConfirmSyncOpen(false);

    // Capture all form values before closing the dialog.
    const policyId = sendPolicyId;
    // CSR-2287: captured here because the dialog closes (and may unmount)
    // before the response lands.
    const sentPolicyIds = [...selectedPolicyIds];
    const expectedPolicyIds = expectedCoiCertificatePolicyIds(checkedPolicies);
    const sentEventProps = selectionEventProps();
    const sentIncludesRenewalOffer = includesRenewalOffer;
    const sentRenewalOnly =
      checkedPolicies.length > 0 &&
      checkedPolicies.every(
        (p) => (p.status ?? "").trim().toLowerCase() === COI_RENEWAL_OFFER_STATUS,
      );
    const holder = { name: certificateHolder, address: fullAddress };
    const capturedHolderId = selectedHolderId;
    // CSR-1453: gate the AI flag on a genuine selection (a picked name), the
    // same condition used for the preview body and the description line. The
    // tab being open is not a selection.
    const hasAdditionalInsured =
      additionalInfo.includes("Additional Insured") &&
      additionalInsuredName.trim().length > 0 &&
      !selectedAdditionalInsuredIsInactive;
    const aiCoverageIds =
      hasAdditionalInsured && additionalInsuredCoverageIds.length > 0
        ? additionalInsuredCoverageIds
        : undefined;
    const hasWaiverOfSubrogation = additionalInfo.includes("Waiver of Subrogation");
    const effectiveWaiverOfSubrogationId =
      hasWaiverOfSubrogation && waiverOfSubrogationId ? waiverOfSubrogationId : null;
    // CSR-1316: same checkbox gate as the preview path.
    const effectiveLossPayeeId =
      additionalInfo.includes("Loss Payee") && lossPayeeId ? lossPayeeId : null;
    const description = buildDescription();
    const additionalInfoSelections: string[] = [];
    if (
      additionalInfo.includes("Vehicles") &&
      effectiveSelectedVehicleIds.length
    ) {
      additionalInfoSelections.push("Vehicles");
    }
    if (
      additionalInfo.includes("Trailers") &&
      effectiveSelectedTrailerIds.length
    ) {
      additionalInfoSelections.push("Trailers");
    }
    if (
      additionalInfo.includes("Drivers") &&
      effectiveSelectedDriverIds.length
    ) {
      additionalInfoSelections.push("Drivers");
    }
    if (hasAdditionalInsured && additionalInsuredName.trim()) {
      additionalInfoSelections.push("Additional Insured");
    }
    if (additionalInfo.includes("Loss Payee") && lossPayeeId) {
      additionalInfoSelections.push("Loss Payee");
    }
    if (additionalInfo.includes("Waiver of Subrogation") && waiverOfSubrogationId) {
      additionalInfoSelections.push("Waiver of Subrogation");
    }
    if (additionalInfo.includes("Special Verbiage") && specialVerbiage.trim()) {
      additionalInfoSelections.push("Special Verbiage");
    }
    const currentInsuredId = insured.id;

    const typedRecipients = collectTypedRecipients();
    const shouldSendCopy = effectiveSendCopy;

    // Close dialog immediately — component may unmount after this.
    onOpenChange(false);

    toast({
      title: "COI Processing",
      // CSR-1405: don't claim we're fetching fresh data when the producer
      // opted to skip the carrier sync.
      description: skipSync
        ? "Your COI is being processed and will be sent shortly."
        : "Your COI is being processed. We're fetching the latest data from the carrier and will send it shortly.",
    });

    const sendConfig: CoiSendConfig = {
      certificateHolder,
      selectedHolderId: capturedHolderId,
      holderAddress,
      holderCity,
      holderState,
      holderZip,
      recipients: typedRecipients,
      sendCopyToInsured: shouldSendCopy,
      additionalInfo,
      selectedVehicleIds: effectiveSelectedVehicleIds,
      selectedDriverIds: effectiveSelectedDriverIds,
      selectedTrailerIds: effectiveSelectedTrailerIds,
      specialVerbiage,
      additionalInsuredName,
      additionalInsuredCoverageIds,
      additionalInsuredSpecialVerbiage,
      lossPayeeId,
      lossPayeeName,
      lossPayeeCoverageIds,
      lossPayeeVehicles,
      lossPayeeSpecialVerbiage,
      waiverOfSubrogationId,
      waiverOfSubrogationName,
      waiverOfSubrogationCoverageIds,
      waiverOfSubrogationSpecialVerbiage,
    };

    (async () => {
      const res = await apiFetch(`/api/policies/${policyId}/send-coi`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          // CSR-778: the canonical anchor lives on the body; the URL's
          // `:policyId` is only the worker's carrier-sync hint.
          insuredId: currentInsuredId,
          // CSR-1097: typed recipients + a separate copy-to-insured flag.
          recipients: typedRecipients,
          sendCopyToInsured: shouldSendCopy,
          // CSR-1405: when true, the worker skips the send-time carrier sync
          // (the producer opted out because the data was already fresh).
          skipSync,
          // CSR-1791: snapshot so this send can later be resent pre-filled.
          sendConfig,
          coiOptions: {
            certificateHolderId: capturedHolderId,
            certificateHolder: holder,
            additionalInsured: hasAdditionalInsured,
            // CSR-2163: names + entity verbiage for the server-side composer.
            additionalInsuredName: hasAdditionalInsured
              ? additionalInsuredName.trim()
              : undefined,
            additionalInsuredSpecialVerbiage: hasAdditionalInsured
              ? additionalInsuredSpecialVerbiage
              : undefined,
            waiverOfSubrogationName: effectiveWaiverOfSubrogationId
              ? waiverOfSubrogationName.trim()
              : undefined,
            waiverOfSubrogationSpecialVerbiage: effectiveWaiverOfSubrogationId
              ? waiverOfSubrogationSpecialVerbiage
              : undefined,
            additionalInsuredCoverageIds: aiCoverageIds,
            waiverOfSubrogation: false,
            description,
            additionalInfo: additionalInfoSelections,
            // CSR-792 — Loss Payee fields (CSR-1316: gated on the checkbox).
            lossPayeeId: effectiveLossPayeeId,
            lossPayeeCoverageIds: effectiveLossPayeeId ? lossPayeeCoverageIds : [],
            lossPayeeVehicles: effectiveLossPayeeId ? lossPayeeVehicles : [],
            lossPayeeSpecialVerbiage: effectiveLossPayeeId ? lossPayeeSpecialVerbiage : null,
            // CSR-1101 — Waiver of Subrogation fields.
            waiverOfSubrogationId: effectiveWaiverOfSubrogationId,
            waiverOfSubrogationCoverageIds: effectiveWaiverOfSubrogationId
              ? waiverOfSubrogationCoverageIds
              : undefined,
          },
          // CSR-2287: see the preview body — always sent while the flag is on.
          ...(policyPickerEnabled ? { selectedPolicyIds: sentPolicyIds } : {}),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Failed to send COI" }));
        // CSR-2287: a rejected selection is the producer's to fix, not a
        // generic send failure — name it and keep the selection as it is.
        if (
          policyPickerEnabled &&
          res.status === 422 &&
          err?.error === "policy_not_selectable"
        ) {
          toast({
            title: "Policy can’t be on this certificate",
            description: COI_POLICY_NOT_SELECTABLE_MESSAGE,
            variant: "destructive",
          });
          return;
        }
        throw new Error(err.error || "Failed to send COI");
      }
      if (policyPickerEnabled) {
        // The 202 carries the policies the service put on EACH recipient's
        // certificate, compared one issuance at a time: a silent difference
        // from what was checked is the failure the producer never notices, and
        // one wrong recipient is enough. CSR-2287.
        const body = await res.json().catch(() => null);
        const policySetMismatch = hasCoiPolicySetMismatch(
          expectedPolicyIds,
          coiIssuedPolicyIdsByIssuance(body),
        );
        if (policySetMismatch) {
          toast({
            title: "Different policies on the certificate",
            description: COI_POLICY_SET_MISMATCH_MESSAGE,
            variant: "destructive",
          });
        }
        trackEvent("COI Sent", {
          ...sentEventProps,
          policy_set_mismatch: policySetMismatch,
        });
        if (sentIncludesRenewalOffer) {
          trackEvent("COI Sent With Renewal Offer", {
            renewal_only: sentRenewalOnly,
            any_missing_limits: sentEventProps.any_missing_limits,
          });
        }
      }
      // CSR-2237 — both history queries, because they are different caches of
      // the same rows: the COIs tab reads a page, the insured timeline reads
      // the whole history. Invalidation matches on key PREFIX, so one call per
      // key covers every page and filter combination under it.
      //
      // This replaces an optimistic prepend that wrote
      // ["insured-coi-history", insuredId] directly. That key was two elements
      // and the hook's is three (the filters are part of it), so the write
      // never landed on the query the tab was rendering — the freshly
      // "PROCESSING" row was arriving from this refetch all along.
      queryClient.invalidateQueries({ queryKey: ["insured-coi-history", currentInsuredId] });
      queryClient.invalidateQueries({ queryKey: ["insured-coi-history-page", currentInsuredId] });
      queryClient.invalidateQueries({ queryKey: ["activity-logs"] });
      // CSR-1445 — also refresh the Insured Detail timeline (its own scoped query).
      queryClient.invalidateQueries({ queryKey: ["insured-activity-logs"] });
    })().catch((err) => {
      toast({ title: "COI Failed", description: (err as Error).message, variant: "destructive" });
    });
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPdfBlob(null);
    setPdfData(null);
  };

  const holderFilled = certificateHolder.trim().length > 0;

  const emptyState = (
    <div className="space-y-3 py-6 text-center">
      <p className="text-sm text-muted-foreground">
        This insured has no COI-eligible policies. Add or activate a policy before sending a Certificate of Insurance.
      </p>
      <div className="flex justify-center pt-2">
        <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
      </div>
    </div>
  );

  const formStep = (
    <div className="space-y-4 pt-2">
      {policyPickerEnabled && (
        <div className="space-y-2">
          <PoliciesOnCertificatePicker
            policies={policies}
            excludeAor={showAor}
            selectedIds={selectedPolicyIds}
            onSelectionChange={handleSelectionChange}
            open={pickerOpen}
            onOpenChange={handlePickerOpenChange}
            emptyErrorRef={emptyErrorRef}
          />
          {!selectionIsDefault && selectedPolicyIds.length > 0 && (
            <p
              className="text-xs text-muted-foreground"
              data-testid="coi-custom-selection-template-notice"
            >
              {COI_CUSTOM_SELECTION_TEMPLATE_NOTICE}
            </p>
          )}
        </div>
      )}

      <div>
        <label className="text-sm font-medium">Certificate Holder</label>
        <div className="mt-1">
          <CertificateHolderPicker
            selectedId={selectedHolderId}
            holderName={certificateHolder}
            holderAddress={fullAddress}
            onNameChange={(name) => {
              setCertificateHolder(name);
              if (selectedHolderId) setSelectedHolderId(null);
            }}
            onAddressChange={() => {
              /* unused -- address fields are separate inputs below */
            }}
            onClear={() => setSelectedHolderId(null)}
            onSelect={(holder: CertificateHolder) => {
              setCertificateHolder(holder.name);
              setSelectedHolderId(holder.id);
              if (holder.address) {
                const { street, city, state, zip } = parseHolderAddress(holder.address);
                setHolderAddress(street);
                setHolderCity(city);
                setHolderState(state);
                setHolderZip(zip);
              }
              if (holder.contactEmail) {
                const email = holder.contactEmail;
                setRecipients((prev) => {
                  const next = [...prev];
                  next[0] = email;
                  return next;
                });
              }
            }}
          />
        </div>
      </div>

      <div>
        <label className="text-sm font-medium">Address{holderFilled && " *"}</label>
        <Input
          className="mt-1"
          value={holderAddress}
          onChange={(e) => setHolderAddress(e.target.value)}
          placeholder="Street address"
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-sm font-medium">City{holderFilled && " *"}</label>
          <Input
            className="mt-1"
            value={holderCity}
            onChange={(e) => setHolderCity(e.target.value)}
            placeholder="City"
          />
        </div>
        <div>
          <label className="text-sm font-medium">State{holderFilled && " *"}</label>
          <select
            className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            value={holderState}
            onChange={(e) => setHolderState(e.target.value)}
          >
            <option value="">Select state...</option>
            <optgroup label="United States">
              {US_STATE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </optgroup>
            <optgroup label="Canada">
              {CA_PROVINCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </optgroup>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">Zip{holderFilled && " *"}</label>
          <Input
            className="mt-1"
            value={holderZip}
            onChange={(e) => setHolderZip(e.target.value)}
            placeholder="Zip"
          />
        </div>
      </div>

      <div>
        <label className="text-sm font-medium">To Email</label>
        <div className="mt-1 space-y-2">
          {recipients.map((email, i) => {
            const invalid = isRecipientInvalid(email);
            return (
              <div key={i}>
                <div className="flex items-center gap-2">
                  <Input
                    className={cn("flex-1", invalid && "border-destructive")}
                    type="email"
                    value={email}
                    onChange={(e) => updateRecipient(i, e.target.value)}
                    placeholder="email@example.com"
                    aria-invalid={invalid || undefined}
                    data-testid={`recipient-input-${i}`}
                  />
                  {recipients.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      aria-label="Remove recipient"
                      data-testid={`recipient-remove-${i}`}
                      onClick={() => removeRecipient(i)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {invalid && (
                  <p
                    className="mt-1 text-xs text-destructive"
                    data-testid={`recipient-error-${i}`}
                  >
                    Enter a valid email
                  </p>
                )}
              </div>
            );
          })}
        </div>
        {recipients.length < 5 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-2 gap-1.5 px-2"
            data-testid="add-recipient"
            onClick={addRecipient}
          >
            <Plus className="w-4 h-4" />
            Add recipient
          </Button>
        )}
      </div>

      {insured.email && (
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={effectiveSendCopy}
            onChange={(e) => {
              setCopyTouched(true);
              setSendCopyToInsured(e.target.checked);
            }}
            className="rounded border-input"
            data-testid="send-copy-to-insured"
          />
          <span>
            Send copy to insured
            <span className="block text-xs text-muted-foreground">
              A copy will be sent to {insured.email}.
            </span>
          </span>
        </label>
      )}

      <div>
        <label className="text-sm font-medium">Additional Information</label>
        <div className="mt-1 flex flex-wrap gap-2">
          {["Vehicles", "Trailers", "Drivers", "Additional Insured", "Loss Payee", "Waiver of Subrogation", "Special Verbiage"].map((opt) => {
            const checked = additionalInfo.includes(opt);
            return (
              <label
                key={opt}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm cursor-pointer transition-colors",
                  checked
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background border-input hover:bg-muted"
                )}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={checked}
                  onChange={() => toggleAdditionalInfo(opt)}
                />
                {opt}
              </label>
            );
          })}
        </div>
      </div>

      {additionalInfo.includes("Vehicles") && (
        <div>
          <label className="text-sm font-medium">Vehicles to include</label>
          <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-input bg-background p-2 space-y-1">
            {vehicles.length === 0 ? (
              <p className="text-xs text-muted-foreground italic px-1 py-1">No active vehicles on this insured's policies.</p>
            ) : (
              vehicles.map((v) => (
                <label key={v.id} className="flex items-center gap-2 px-2 py-1 text-sm rounded hover:bg-muted cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedVehicleIds.includes(v.id)}
                    onChange={() => toggleVehicle(v.id)}
                  />
                  {v.label}
                </label>
              ))
            )}
          </div>
        </div>
      )}

      {additionalInfo.includes("Trailers") && (
        <div>
          <label className="text-sm font-medium">Trailers to include</label>
          <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-input bg-background p-2 space-y-1">
            {trailers.length === 0 ? (
              <p className="text-xs text-muted-foreground italic px-1 py-1">No active trailers on this insured's policies.</p>
            ) : (
              trailers.map((t) => (
                <label key={t.id} className="flex items-center gap-2 px-2 py-1 text-sm rounded hover:bg-muted cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedTrailerIds.includes(t.id)}
                    onChange={() => toggleTrailer(t.id)}
                  />
                  {t.label}
                </label>
              ))
            )}
          </div>
        </div>
      )}

      {additionalInfo.includes("Drivers") && (
        <div>
          <label className="text-sm font-medium">Drivers to include</label>
          <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-input bg-background p-2 space-y-1">
            {drivers.length === 0 ? (
              <p className="text-xs text-muted-foreground italic px-1 py-1">No active drivers on this insured's policies.</p>
            ) : (
              drivers.map((d) => (
                <label key={d.id} className="flex items-center gap-2 px-2 py-1 text-sm rounded hover:bg-muted cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedDriverIds.includes(d.id)}
                    onChange={() => toggleDriver(d.id)}
                  />
                  {d.label}
                </label>
              ))
            )}
          </div>
        </div>
      )}

      {additionalInfo.includes("Additional Insured") && (
        <div>
          <label className="text-sm font-bold">Pick an Additional Insured</label>
          <p className="text-xs text-muted-foreground mt-1">
            Select an Additional Insured already linked to this insured. To add a new one, use the Assets tab. The selection is added to the Description of Operations only — the Certificate Holder above is not changed.
          </p>
          {(() => {
            type PickerEntry = {
              name: string;
              email?: string | null;
              address?: string | null;
              pending: boolean;
              // CSR-2274 — a carrier snapshot exists for every eligible
              // policy and none names this holder. Never true for the
              // policy-sync entries below: they exist because the carrier
              // listed them.
              inactive: boolean;
              coverageIds?: string[];
              specialVerbiage?: string | null;
            };
            const entries: PickerEntry[] = [];
            const seen = new Set<string>();

            const aiLinks = additionalInsuredLinks ?? [];
            for (const link of aiLinks) {
              if (!link.name || seen.has(link.name)) continue;
              seen.add(link.name);
              const hasAddress = !!(link.street && link.city && link.state && link.zip);
              const coverageIds = Array.isArray(link.coverageIds)
                ? link.coverageIds.map(String)
                : [];
              const addressLine = hasAddress
                ? `${link.city}, ${link.state}`
                : null;
              entries.push({
                name: link.name,
                email: link.email,
                address: addressLine,
                pending: !hasAddress || coverageIds.length === 0,
                inactive: !!link.inactive,
                coverageIds,
                specialVerbiage: link.specialVerbiage ?? null,
              });
            }

            // DC-synced entries across every policy.
            // CSR-1062: policy-sync AIs never carry `coverageIds`
            // (`PolicyAdditionalInsured` has no such field), so they can
            // never produce a coherent COI on their own — without
            // coverages the backend marks ADDL INSD = N everywhere while
            // the name still flows into Description of Operations. Force
            // every policy-sync entry to render as "Pending Additional
            // Information" so the user must promote it to a CRUD link
            // via the Assets tab (which collects name + address + at
            // least one coverage) before using it on a COI.
            for (const p of certificatePolicies) {
              const ai = p.additionalInsureds as PolicyAdditionalInsured[] | null;
              if (!Array.isArray(ai)) continue;
              for (const item of ai) {
                if (!item?.name || seen.has(item.name)) continue;
                seen.add(item.name);
                const hasAddr = !!(item.street && item.city && item.state && item.zip);
                entries.push({
                  name: item.name,
                  email: null,
                  address: hasAddr
                    ? `${item.street}, ${item.city}, ${item.state} ${item.zip}`
                    : null,
                  pending: true,
                  inactive: false,
                });
              }
            }

            // CSR-2274 — Active entries first, Inactive after, as on the Assets
            // tab; the sort is stable, so each group keeps the order above.
            entries.sort((a, b) => Number(a.inactive) - Number(b.inactive));

            if (entries.length === 0) {
              return (
                <p className="mt-2 text-sm text-muted-foreground italic">
                  No additional insureds linked to this insured.
                </p>
              );
            }
            return (
              <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-input bg-background space-y-1 p-2">
                {entries.map((entry, idx) => {
                  const isSelected = additionalInsuredName === entry.name;
                  const subtitle = [entry.email, entry.address].filter(Boolean).join(" - ") || "No email";
                  return (
                    <button
                      key={`ai-${idx}`}
                      type="button"
                      disabled={entry.inactive || entry.pending}
                      title={
                        // CSR-2274 — Inactive outranks Pending: a link can be
                        // both missing data AND dropped by the carrier, and
                        // completing the missing data would not make it
                        // usable again, so "blocked" beats "needs attention".
                        entry.inactive
                          ? "This Additional Insured is Inactive and cannot be used until it is Active again."
                          : entry.pending
                            ? "Complete the missing address or coverages before using this Additional Insured"
                            : undefined
                      }
                      className={cn(
                        "w-full text-left px-2 py-1.5 text-sm rounded transition-colors",
                        entry.inactive || entry.pending
                          ? "opacity-60 cursor-not-allowed bg-muted/30"
                          : isSelected
                            ? "bg-muted cursor-pointer"
                            : "hover:bg-muted cursor-pointer"
                      )}
                      onClick={() => {
                        if (entry.inactive || entry.pending) return;
                        if (isSelected) {
                          setAdditionalInsuredName("");
                          setAdditionalInsuredCoverageIds([]);
                          setAdditionalInsuredSpecialVerbiage(null);
                        } else {
                          setAdditionalInsuredName(entry.name);
                          setAdditionalInsuredCoverageIds(entry.coverageIds ?? []);
                          setAdditionalInsuredSpecialVerbiage(entry.specialVerbiage ?? null);
                        }
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <div className="font-medium flex-1">{entry.name}</div>
                        {entry.inactive ? (
                          <span
                            data-testid="additional-insured-inactive-chip"
                            className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-500/10 text-red-700 dark:text-red-400 shrink-0"
                          >
                            Inactive
                          </span>
                        ) : entry.pending ? (
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-400 shrink-0">
                            Pending Additional Information
                          </span>
                        ) : null}
                        {isSelected && !entry.inactive && !entry.pending && (
                          <Check className="w-4 h-4 text-primary shrink-0" />
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {subtitle}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {additionalInfo.includes("Loss Payee") && (
        <div data-testid="loss-payee-picker">
          <label className="text-sm font-bold">Pick a Loss Payee</label>
          <p className="text-xs text-muted-foreground mt-1">
            Select a Loss Payee already linked to this insured. To add a new one, use the Assets tab. The selection is added to the Description of Operations only — the Certificate Holder above is not changed.
          </p>
          {(() => {
            const entries: LossPayeeLink[] = lossPayeeLinks ?? [];
            // CSR-2212 — say "still loading", never "there are none".
            if (lossPayeesLoading) {
              return (
                <div className="mt-2 rounded-md border border-input bg-background p-4">
                  <p className="text-sm text-muted-foreground italic">
                    Loading loss payees…
                  </p>
                </div>
              );
            }
            if (entries.length === 0) {
              return (
                <div className="mt-2 rounded-md border border-input bg-background p-4">
                  <p className="text-sm text-muted-foreground italic">
                    No loss payees linked to this insured. Add one from the Assets tab.
                  </p>
                </div>
              );
            }
            return (
              <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-input bg-background space-y-1 p-2">
                {entries.map((lp) => {
                  const isSelected = lossPayeeId === lp.id;
                  const subtitle = `${lp.email || "No email"} · ${lp.city || ""}${lp.city && lp.state ? ", " : ""}${lp.state || ""}`;
                  return (
                    <button
                      key={`lp-${lp.id}`}
                      type="button"
                      disabled={lp.pendingAdditionalInfo}
                      title={
                        lp.pendingAdditionalInfo
                          ? "Complete the missing fields on this Loss Payee before using it"
                          : undefined
                      }
                      data-testid={`loss-payee-row-${lp.id}`}
                      className={cn(
                        "w-full text-left px-2 py-1.5 text-sm rounded transition-colors",
                        lp.pendingAdditionalInfo
                          ? "opacity-60 cursor-not-allowed bg-muted/30"
                          : isSelected
                            ? "bg-muted cursor-pointer"
                            : "hover:bg-muted cursor-pointer",
                      )}
                      onClick={() => {
                        if (lp.pendingAdditionalInfo) return;
                        if (isSelected) {
                          setLossPayeeId(null);
                          setLossPayeeName("");
                          setLossPayeeCoverageIds([]);
                          setLossPayeeVehicles([]);
                          setLossPayeeSpecialVerbiage(null);
                        } else {
                          setLossPayeeId(lp.id);
                          setLossPayeeName(lp.name);
                          setLossPayeeCoverageIds(lp.coverageIds);
                          setLossPayeeVehicles(
                            lp.vehicles
                              .filter((v) => v.raw && !v.raw.removedAt)
                              .map((v) => v.raw),
                          );
                          setLossPayeeSpecialVerbiage(lp.specialVerbiage);
                        }
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <div className="font-medium flex-1">{lp.name}</div>
                        {lp.pendingAdditionalInfo && (
                          <span
                            data-testid="loss-payee-pending-chip"
                            className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-400 shrink-0"
                          >
                            Pending Additional Information
                          </span>
                        )}
                        {isSelected && !lp.pendingAdditionalInfo && (
                          <Check className="w-4 h-4 text-primary shrink-0" />
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {subtitle}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {additionalInfo.includes("Waiver of Subrogation") && (
        <div data-testid="waiver-of-subrogation-picker">
          <label className="text-sm font-bold">Pick a Waiver of Subrogation</label>
          <p className="text-xs text-muted-foreground mt-1">
            Select a Waiver of Subrogation already linked to this insured. To add a new one, use the Assets tab. The selection marks SUBR WVD and ADDL INSD on the linked coverage rows and adds a line to the Description of Operations — the Certificate Holder above is not changed.
          </p>
          {(() => {
            const entries: WaiverOfSubrogationLink[] = waiverOfSubrogationLinks ?? [];
            // CSR-2212 — as above.
            if (waiversLoading) {
              return (
                <div className="mt-2 rounded-md border border-input bg-background p-4">
                  <p className="text-sm text-muted-foreground italic">
                    Loading waivers of subrogation…
                  </p>
                </div>
              );
            }
            if (entries.length === 0) {
              return (
                <div className="mt-2 rounded-md border border-input bg-background p-4">
                  <p className="text-sm text-muted-foreground italic">
                    No waivers of subrogation linked to this insured. Add one from the Assets tab.
                  </p>
                </div>
              );
            }
            return (
              <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-input bg-background space-y-1 p-2">
                {entries.map((wos) => {
                  const isSelected = waiverOfSubrogationId === wos.id;
                  const subtitle = `${wos.email || "No email"} · ${wos.city || ""}${wos.city && wos.state ? ", " : ""}${wos.state || ""}`;
                  return (
                    <button
                      key={`wos-${wos.id}`}
                      type="button"
                      disabled={wos.pendingAdditionalInfo}
                      title={
                        wos.pendingAdditionalInfo
                          ? "Complete the missing fields on this Waiver of Subrogation before using it"
                          : undefined
                      }
                      data-testid={`waiver-of-subrogation-row-${wos.id}`}
                      className={cn(
                        "w-full text-left px-2 py-1.5 text-sm rounded transition-colors",
                        wos.pendingAdditionalInfo
                          ? "opacity-60 cursor-not-allowed bg-muted/30"
                          : isSelected
                            ? "bg-muted cursor-pointer"
                            : "hover:bg-muted cursor-pointer",
                      )}
                      onClick={() => {
                        if (wos.pendingAdditionalInfo) return;
                        if (isSelected) {
                          setWaiverOfSubrogationId(null);
                          setWaiverOfSubrogationName("");
                          setWaiverOfSubrogationCoverageIds([]);
                          setWaiverOfSubrogationSpecialVerbiage(null);
                        } else {
                          setWaiverOfSubrogationId(wos.id);
                          setWaiverOfSubrogationName(wos.name);
                          setWaiverOfSubrogationCoverageIds(wos.coverageIds);
                          setWaiverOfSubrogationSpecialVerbiage(wos.specialVerbiage);
                        }
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <div className="font-medium flex-1">{wos.name}</div>
                        {wos.pendingAdditionalInfo && (
                          <span
                            data-testid="waiver-of-subrogation-pending-chip"
                            className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 dark:text-amber-400 shrink-0"
                          >
                            Pending Additional Information
                          </span>
                        )}
                        {isSelected && !wos.pendingAdditionalInfo && (
                          <Check className="w-4 h-4 text-primary shrink-0" />
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {subtitle}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {additionalInfo.includes("Special Verbiage") && (
        <div>
          <label className="text-sm font-medium">Special Verbiage</label>
          <textarea
            className="mt-1 w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-y focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            value={specialVerbiage}
            onChange={(e) => setSpecialVerbiage(e.target.value)}
            placeholder="Additional remarks, endorsements, special wording, etc."
          />
        </div>
      )}

      <div className="flex justify-between pt-2">
        <Button
          variant="outline"
          className={cn("gap-1.5", selectionIsEmpty && "opacity-50")}
          disabled={previewLoading}
          aria-disabled={selectionIsEmpty || undefined}
          onClick={handlePreview}
        >
          <FileText className="w-4 h-4" />
          {previewLoading ? "Generating..." : "Preview COI"}
        </Button>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className={cn(selectionIsEmpty && "opacity-50")}
            aria-disabled={selectionIsEmpty || undefined}
            onClick={handleSend}
          >
            Send COI
          </Button>
        </div>
      </div>
    </div>
  );

  // CSR-2287: with the picker on, "nothing to send" means no SELECTABLE term —
  // a future renewal offer alone is now a certificate the producer can send,
  // and a non-renewal term alone is not.
  const hasSomethingToSend = policyPickerEnabled
    ? selectablePolicies.length > 0
    : eligiblePolicies.length > 0;
  const content = !hasSomethingToSend ? emptyState : formStep;

  const downloadFilename = `COI-${(insured.name ?? "insured").replace(/[^a-zA-Z0-9-_]+/g, "_")}-${new Date().toISOString().slice(0, 10)}.pdf`;

  const previewModal = (
    <COIPreviewModal
      open={previewOpen}
      onOpenChange={(next) => {
        if (!next) closePreview();
        else setPreviewOpen(true);
      }}
      pdfData={pdfData}
      pdfBlob={pdfBlob}
      downloadFilename={downloadFilename}
    />
  );

  // CSR-1405: shown when the primary policy was scraped within the recency
  // window. "Sync and send" runs the carrier sync first (current behavior);
  // "Send without syncing" skips it for a near-instant send.
  const confirmSyncModal = (
    <AlertDialog open={confirmSyncOpen} onOpenChange={setConfirmSyncOpen}>
      <AlertDialogContent data-testid="coi-sync-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Sync the policy before sending?</AlertDialogTitle>
          <AlertDialogDescription>
            This policy was updated within the last hour, so its data is likely
            already current. Would you like to sync the policy again before
            sending the COI?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="coi-sync-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="coi-sync-and-send"
            onClick={() => performSend(false)}
          >
            Sync and send
          </AlertDialogAction>
          <AlertDialogAction
            data-testid="coi-send-without-sync"
            onClick={() => performSend(true)}
          >
            Send without syncing
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (isMobile) {
    return (
      <>
        <Drawer open={open} onOpenChange={onOpenChange}>
          <DrawerContent className="max-h-[90vh]" aria-describedby={undefined}>
            <DrawerHeader>
              <DrawerTitle className="text-base font-bold">{isResend ? "Resend COI" : "Send New COI"}</DrawerTitle>
            </DrawerHeader>
            <div className="px-4 pb-4 overflow-y-auto flex-1">
              {content}
            </div>
          </DrawerContent>
        </Drawer>
        {previewModal}
        {confirmSyncModal}
      </>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" data-testid="dialog-send-coi">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">{isResend ? "Resend COI" : "Send New COI"}</DialogTitle>
            <DialogDescription>{isResend ? "Review the pre-filled details and resend this certificate of insurance." : "Create and send a new certificate of insurance."}</DialogDescription>
          </DialogHeader>
          {/* CSR-2298 — no scroll container here: DialogContent already caps
              its height and scrolls (CSR-1141). A second one nested inside made
              the dialog's grid row size itself to the full form while the form
              clamped itself to 70vh — two scrollbars, a list cut mid-row, and
              an empty band under it once Additional Information added height. */}
          <div className="pr-1">
            {content}
          </div>
        </DialogContent>
      </Dialog>
      {previewModal}
      {confirmSyncModal}
    </>
  );
}
